import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { graphGet } from '../../../api/graphClient';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import {
  WorkCalendar, ResourceCalendarAssignment,
  listCalendars, listResourceCalendars, createResourceCalendar, deleteResourceCalendar,
} from '../../../services/calendarService';

/** Project Settings → Resource Calendars. Assign a specific work calendar to a
 *  specific worker for THIS project (with an optional effective date range),
 *  list and remove assignments. Falls back to the tenant default / hardcoded
 *  Mon–Fri when a worker has no assignment (see calendarService fallback chain). */
export default function ResourceCalendarsSection({ pca, projectId }: { pca: IPublicClientApplication; projectId: string }) {
  const [assigns, setAssigns] = useState<ResourceCalendarAssignment[] | null>(null);
  const [cals, setCals] = useState<WorkCalendar[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [worker, setWorker] = useState<Person | null>(null);
  const [calId, setCalId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [a, c] = await Promise.all([listResourceCalendars(pca, projectId), listCalendars(pca)]);
      setAssigns(a); setCals(c);
      // best-effort resolve worker display names for the list
      const need = Array.from(new Set(a.map(x => x.dagrun_pm_userid).filter((x): x is string => !!x)));
      const resolved: Record<string, string> = {};
      await Promise.all(need.map(async oid => {
        try { const u = await graphGet<{ displayName: string }>(pca, `/users/${oid}?$select=displayName`); resolved[oid] = u.displayName; }
        catch { resolved[oid] = oid.slice(0, 8) + '…'; }
      }));
      setNames(resolved);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load.'); setAssigns([]); }
  }, [pca, projectId]);
  useEffect(() => { load(); }, [load]);

  const calName = (id: string | null) => cals.find(c => c.dagrun_pm_calendarid === id)?.dagrun_pm_name ?? '(calendar removed)';

  const add = async () => {
    if (!worker || !calId || busy) { if (!worker || !calId) setError('Pick a worker and a calendar.'); return; }
    setBusy(true); setError(null);
    try {
      await createResourceCalendar(pca, { projectId, userId: worker.aadObjectId, calendarId: calId, from: from || null, to: to || null });
      setWorker(null); setCalId(''); setFrom(''); setTo('');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to add assignment.'); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try { await deleteResourceCalendar(pca, id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to remove.'); }
    finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--ink)' };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>RESOURCE CALENDARS</div>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Assign a specific work calendar to a worker on this project. Workers without an
        assignment use the tenant default calendar (or Mon–Fri, 8h if none is set).
      </p>
      {error && <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 10 }}>{error}</div>}

      {assigns === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : assigns.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>No per-worker calendar assignments on this project yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 11, textTransform: 'uppercase' }}>
            <th style={{ padding: '6px 8px' }}>Worker</th><th style={{ padding: '6px 8px' }}>Calendar</th>
            <th style={{ padding: '6px 8px' }}>Effective</th><th />
          </tr></thead>
          <tbody>
            {assigns.map(a => (
              <tr key={a.dagrun_pm_resourcecalendarid} style={{ borderTop: '0.5px solid var(--border-light)' }}>
                <td style={{ padding: '8px', color: 'var(--ink)' }}>{a.dagrun_pm_userid ? (names[a.dagrun_pm_userid] ?? a.dagrun_pm_userid.slice(0, 8) + '…') : '—'}</td>
                <td style={{ padding: '8px', color: 'var(--ink-2)' }}>{calName(a._dagrun_pm_calendarid_value)}</td>
                <td style={{ padding: '8px', color: 'var(--ink-3)', fontSize: 12 }}>
                  {a.dagrun_pm_effectivefrom || a.dagrun_pm_effectiveto
                    ? `${a.dagrun_pm_effectivefrom?.slice(0, 10) ?? '…'} → ${a.dagrun_pm_effectiveto?.slice(0, 10) ?? '…'}`
                    : 'Always'}
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  <button type="button" title="Remove assignment" onClick={() => remove(a.dagrun_pm_resourcecalendarid)} disabled={busy}
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15 }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Worker</label>
          <PeoplePicker pca={pca} value={worker} onChange={setWorker} placeholder="Search Microsoft 365 users…" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Calendar</label>
          <select value={calId} onChange={e => setCalId(e.target.value)} style={{ ...inp, width: '100%' }} disabled={cals.length === 0}>
            <option value="">{cals.length === 0 ? 'No calendars — create one in Admin' : 'Select a calendar…'}</option>
            {cals.map(c => <option key={c.dagrun_pm_calendarid} value={c.dagrun_pm_calendarid}>{c.dagrun_pm_name}{c.dagrun_pm_isdefault ? ' (default)' : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Effective from (optional)</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inp, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Effective to (optional)</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inp, width: '100%' }} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <button className="btn-primary" onClick={add} disabled={!worker || !calId || busy}>{busy ? 'Adding…' : 'Assign calendar'}</button>
      </div>
    </div>
  );
}
